import { useState, useEffect } from "react";
import {
  getAssignments,
  createAssignmentWithRules,
  createAssignmentWithOverride,
  updateAssignment,
  deleteAssignment,
  getShifts,
  getStaffList,
  getShiftTasks,
  extractErrorMessage,
} from "../lib/api";
import {
  PageHeader,
  DataTable,
  StatusBadge,
  LoadingSpinner,
  EmptyState,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { RulePromptDialog } from "../components/shared/RulePromptDialog";
import { DestructiveConfirmDialog } from "../components/shared/DestructiveConfirmDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Calendar, Pencil, Trash2, ShieldAlert, Loader2, Lock } from "lucide-react";

export default function AssignmentsPage() {
  const { formatDate, formatTime, formatCurrency, currencySymbol } = useFormatters();
  const [assignments, setAssignments] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [shiftTasks, setShiftTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  
  // Rule prompt state
  const [rulePromptOpen, setRulePromptOpen] = useState(false);
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const [rulePrompts, setRulePrompts] = useState([]);
  const [availabilityConflicts, setAvailabilityConflicts] = useState([]);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [ruleLoading, setRuleLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    shift_id: "",
    staff_id: "",
    shift_task_id: "",
    status: "pending",
    pay_rate_override: "",
    notes: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.shift_id) {
      loadShiftTasks(formData.shift_id);
    }
  }, [formData.shift_id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assignRes, shiftRes, staffRes] = await Promise.all([
        getAssignments(),
        getShifts(),
        getStaffList({ limit: 1000 }),
      ]);
      setAssignments(assignRes.data);
      setShifts(shiftRes.data);
      // Handle paginated response format
      const staffData = staffRes.data;
      setStaff(staffData.items || staffData);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadShiftTasks = async (shiftId) => {
    try {
      const response = await getShiftTasks({ shift_id: shiftId });
      setShiftTasks(response.data);
    } catch (err) {
      console.error("Failed to load shift tasks", err);
    }
  };

  const getShiftName = (shiftId) => {
    const shift = shifts.find((s) => s.id === shiftId);
    return shift ? `${shift.name} (${shift.date})` : "Unknown";
  };

  const getStaffName = (staffId) => {
    const member = staff.find((s) => s.id === staffId);
    return member ? `${member.first_name} ${member.last_name}` : "Unknown";
  };

  const handleOpenDialog = (assignment = null) => {
    if (assignment) {
      setSelectedAssignment(assignment);
      setFormData({
        shift_id: assignment.shift_id,
        staff_id: assignment.staff_id,
        shift_task_id: assignment.shift_task_id || "",
        status: assignment.status,
        pay_rate_override: assignment.pay_rate_override?.toString() || "",
        notes: assignment.notes || "",
      });
    } else {
      setSelectedAssignment(null);
      setFormData({
        shift_id: "",
        staff_id: "",
        shift_task_id: "",
        status: "pending",
        pay_rate_override: "",
        notes: "",
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = {
        ...formData,
        shift_task_id: formData.shift_task_id || null,
        pay_rate_override: formData.pay_rate_override
          ? parseFloat(formData.pay_rate_override)
          : null,
      };
      
      if (selectedAssignment) {
        // Updates don't go through rule evaluation
        await updateAssignment(selectedAssignment.id, data);
        toast.success("Assignment updated successfully");
        setDialogOpen(false);
        loadData();
      } else {
        // New assignments go through rule evaluation
        const response = await createAssignmentWithRules(data);
        const result = response.data;
        
        // Check if there are rule prompts or availability conflicts
        const hasPrompts = result.rule_prompts?.length > 0;
        const hasConflicts = result.availability_conflicts?.length > 0;
        
        if (result.requires_confirmation && (hasPrompts || hasConflicts)) {
          // Store the assignment and prompts, show rule dialog
          setPendingAssignment(result.assignment);
          setRulePrompts(result.rule_prompts || []);
          setAvailabilityConflicts(result.availability_conflicts || []);
          setPendingFormData(data);
          setDialogOpen(false);
          setRulePromptOpen(true);
        } else {
          toast.success("Assignment created successfully");
          setDialogOpen(false);
          loadData();
        }
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle accepting all rule prompts for assignments
  const handleRuleAcceptAll = async (acceptances) => {
    // For assignments, accepting means the assignment is already created
    // We just close the dialog and refresh
    toast.success("Assignment created with rules acknowledged");
    setRulePromptOpen(false);
    setPendingAssignment(null);
    setRulePrompts([]);
    setAvailabilityConflicts([]);
    setPendingFormData(null);
    loadData();
  };

  // Handle overriding with availability conflicts
  const handleRuleOverride = async (acceptances, globalReason) => {
    if (!pendingFormData) return;
    
    setRuleLoading(true);
    try {
      // Re-create assignment with override
      await createAssignmentWithOverride(pendingFormData, globalReason);
      toast.success("Assignment created with override");
      setRulePromptOpen(false);
      setPendingAssignment(null);
      setRulePrompts([]);
      setAvailabilityConflicts([]);
      setPendingFormData(null);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setRuleLoading(false);
    }
  };

  // Handle canceling rule prompts
  const handleRuleCancel = () => {
    toast.info("Assignment created - review pending conflicts");
    setRulePromptOpen(false);
    setPendingAssignment(null);
    setRulePrompts([]);
    setAvailabilityConflicts([]);
    setPendingFormData(null);
    loadData();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAssignment(selectedAssignment.id);
      toast.success("Assignment deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedAssignment(null);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  // Check if assignment is locked (completed)
  const isAssignmentLocked = (assignment) => {
    return assignment && assignment.status === "completed";
  };

  const columns = [
    {
      key: "shift_id",
      label: "Shift",
      render: (value) => getShiftName(value),
    },
    {
      key: "staff_id",
      label: "Crew",
      render: (value) => getStaffName(value),
    },
    {
      key: "status",
      label: "Status",
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: "pay_rate_override",
      label: "Rate Override",
      align: "right",
      render: (value) =>
        value ? <span className="font-mono">{formatCurrency(value)}/hr</span> : "-",
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (_, row) => {
        const locked = isAssignmentLocked(row);
        return (
          <div className="flex items-center justify-end gap-2">
            {locked ? (
              <Button
                variant="ghost"
                size="icon"
                disabled
                title="Completed assignments cannot be edited"
              >
                <Lock className="h-4 w-4 text-muted-foreground" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDialog(row);
                }}
                data-testid={`edit-assignment-${row.id}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {locked ? (
              <Button
                variant="ghost"
                size="icon"
                disabled
                title="Completed assignments cannot be deleted"
              >
                <Trash2 className="h-4 w-4 text-muted-foreground/30" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedAssignment(row);
                  setDeleteDialogOpen(true);
                }}
                data-testid={`delete-assignment-${row.id}`}
              >
                <Trash2 className="h-4 w-4 text-red-400" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const canCreate = shifts.length > 0 && staff.length > 0;

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div data-testid="assignments-page">
      <PageHeader
        title="Assignments"
        subtitle="Crew to shift assignments"
        actions={
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium tracking-wide uppercase text-xs px-6"
            data-testid="add-assignment-btn"
            disabled={!canCreate}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Assignment
          </Button>
        }
      />

      {!canCreate && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-4 mb-6 text-amber-500 text-sm">
          You need to create shifts and staff before making assignments.
        </div>
      )}

      <div className="bg-card border border-white/5 rounded-sm">
        {assignments.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="No assignments yet"
            description="Assign crew members to shifts."
            action={
              canCreate && (
                <Button
                  onClick={() => handleOpenDialog()}
                  className="bg-primary text-primary-foreground"
                  data-testid="empty-add-assignment-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Assignment
                </Button>
              )
            }
          />
        ) : (
          <DataTable columns={columns} data={assignments} />
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-secondary text-xl uppercase tracking-wide">
              {selectedAssignment ? "Edit Assignment" : "Add Assignment"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="shift_id">Shift *</Label>
              <Select
                value={formData.shift_id}
                onValueChange={(value) => setFormData({ ...formData, shift_id: value, shift_task_id: "" })}
              >
                <SelectTrigger data-testid="assignment-shift-select">
                  <SelectValue placeholder="Select shift" />
                </SelectTrigger>
                <SelectContent>
                  {shifts.map((shift) => (
                    <SelectItem key={shift.id} value={shift.id}>
                      {shift.name} ({shift.date})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="staff_id">Crew Member *</Label>
              <Select
                value={formData.staff_id}
                onValueChange={(value) => setFormData({ ...formData, staff_id: value })}
              >
                <SelectTrigger data-testid="assignment-staff-select">
                  <SelectValue placeholder="Select crew member" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {shiftTasks.length > 0 && (
              <div>
                <Label htmlFor="shift_task_id">Shift Task (optional)</Label>
                <Select
                  value={formData.shift_task_id}
                  onValueChange={(value) => setFormData({ ...formData, shift_task_id: value })}
                >
                  <SelectTrigger data-testid="assignment-task-select">
                    <SelectValue placeholder="Select task" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {shiftTasks.map((task) => (
                      <SelectItem key={task.id} value={task.id}>
                        Task #{task.id.slice(0, 8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="assignment-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="no_show">No Show</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="pay_rate_override">Pay Rate Override ({currencySymbol}/hr)</Label>
              <Input
                id="pay_rate_override"
                type="number"
                step="0.01"
                min="0"
                value={formData.pay_rate_override}
                onChange={(e) => setFormData({ ...formData, pay_rate_override: e.target.value })}
                placeholder="Leave empty for default"
                data-testid="assignment-rate-input"
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes"
                rows={2}
                data-testid="assignment-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground" data-testid="assignment-submit-btn" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {selectedAssignment ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DestructiveConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Assignment"
        itemName={`${getStaffName(selectedAssignment?.staff_id)} → ${getShiftName(selectedAssignment?.shift_id)}`}
        itemType="assignment"
        description="Are you sure you want to delete this assignment? The crew member will be unassigned from this shift. This action is permanent and cannot be undone."
        onConfirm={handleDelete}
        loading={deleting}
      />

      {/* Rule Prompt Dialog */}
      <RulePromptDialog
        open={rulePromptOpen}
        onOpenChange={setRulePromptOpen}
        entityType="assignment"
        entity={pendingAssignment}
        rulePrompts={rulePrompts}
        availabilityConflicts={availabilityConflicts}
        onAcceptAll={handleRuleAcceptAll}
        onOverride={handleRuleOverride}
        onCancel={handleRuleCancel}
        loading={ruleLoading}
      />
    </div>
  );
}
